import { MAX_MESSAGE_CHARS, THREAD_TOPICS } from '@zuruflex/types';
import { normaliseMessage } from './messaging-rules.js';
/**
 * Topics a renter is never the right person to raise.
 *
 * `PARTNER` is the owner-facing heading — being a partner, the fleet, a
 * payout, the terms somebody publishes. A renter with a complaint about
 * the person they hired from is raising it about their *booking*, which is
 * the thread an agent can actually open the hire from; filing it under
 * `PARTNER` would route it to the team that answers owners and lose the
 * booking reference on the way.
 *
 * A list of what is taken away rather than a second list of what is left,
 * so a topic added to `THREAD_TOPICS` tomorrow appears on both sides by
 * default. A new heading being visible to somebody who should not have it
 * is a misfiled ticket; a new heading silently missing from a surface is a
 * question nobody can ask at all.
 */
const NOT_FOR_CUSTOMERS = ['PARTNER'];
/**
 * The ordered topic list one side may file under.
 *
 * Filtered from the one `THREAD_TOPICS` constant, never respelled. The
 * order is that constant's order on every surface — a web `Select`, a run
 * of radio rows on a phone and the admin filter all read top to bottom in
 * the same sequence, so somebody who has learned where "A payment" sits
 * finds it in the same place on their phone. A screen that hardcodes its
 * own list is the bug this function exists to make impossible, and the
 * labels for these live in `common:support.topic.*` — the values here are
 * enum members and are never shown to anybody.
 */
export function SUPPORT_TOPICS_FOR(realm) {
    if (realm === 'PARTNER')
        return [...THREAD_TOPICS];
    return THREAD_TOPICS.filter((topic) => !NOT_FOR_CUSTOMERS.includes(topic));
}
/**
 * What a contextual entry point pre-selects.
 *
 * A booking wins over a car, and every booking has a car, so the order of
 * these two tests is the whole rule: somebody who tapped Help from a hire
 * is asking about the hire — the dates, the money, the handover — and
 * would have tapped it from the listing if they meant the car itself.
 *
 * `OTHER` for a request from a support screen that arrived with nothing,
 * which is the same default the API applies when a client sends no topic.
 * The two agreeing matters: a form that pre-selects `BOOKING` and posts
 * without one would file it under `OTHER` anyway, and the requester would
 * watch their answer contradict the dropdown they just read.
 *
 * A pre-selection and not a decision — every surface leaves the control
 * editable, because the person writing knows what this is about and we are
 * guessing.
 */
export function topicForContext(context) {
    if (context.bookingId)
        return 'BOOKING';
    if (context.vehicleId)
        return 'VEHICLE';
    return 'OTHER';
}
/**
 * What a compose screen starts on, for one realm and one door.
 *
 * `topicForContext` answers only half the question: it knows what a hire or
 * a car implies and nothing about who is asking. A bare Ask Zuruflex — the
 * account menu, the chat tab's head, the help page — arrives with neither,
 * and the two owner surfaces had each decided for themselves what that
 * means. The portal opened on `PARTNER`; the owner's phone opened on
 * `OTHER`. One owner, one question, two desks, depending on which screen
 * they happened to be holding.
 *
 * So the realm decides it, here, once. An owner with nothing else to go on
 * is asking about being an owner, which is exactly what the `PARTNER`
 * heading means and the only topic a renter may not file under. A renter
 * with nothing to go on could be asking about anything, which is what
 * `OTHER` means — and is also what the API defaults to, so a form that
 * posted without a topic would land in the same place the control showed.
 *
 * A pre-selection and not a decision: every surface leaves the control
 * editable, because the person writing knows what this is about and we are
 * guessing.
 */
export function defaultSupportTopic(realm, context) {
    if (context.bookingId || context.vehicleId)
        return topicForContext(context);
    return realm === 'PARTNER' ? 'PARTNER' : 'OTHER';
}
// ── What may be sent ─────────────────────────────────────────
/**
 * The subject bound, mirrored from `OpenThreadDto`.
 *
 * Here as a constant because five forms need to draw a counter with it and
 * the alternative is five literals, four of which stay right.
 */
export const MAX_SUPPORT_SUBJECT_CHARS = 160;
/**
 * One validator, five forms.
 *
 * The bounds are `OpenThreadDto`'s bounds and `MAX_MESSAGE_CHARS`, because
 * a client that allows what the server refuses is a form that fails on
 * submit — and it fails after the keyboard has gone down, on a screen
 * whose error state is the one nobody designed. Being *stricter* than the
 * server is fine and is deliberate in two places below; being looser never
 * is.
 *
 * Measured on the normalised body rather than on the raw one. The API
 * counts the string it is handed, and what a composer hands it has already
 * been through `normaliseMessage` — so counting the raw text would refuse
 * a message that would have fitted once its stray returns collapsed, which
 * is a form calling somebody a liar about a limit.
 *
 * Every problem is collected rather than the first returned: a form that
 * reveals its objections one at a time is a form somebody submits four
 * times.
 */
export function validateSupportRequest(input) {
    const problems = [];
    const topic = input.topic ?? null;
    if (topic === null) {
        problems.push({ field: 'topic', code: 'required' });
    }
    else if (!THREAD_TOPICS.includes(topic)) {
        // Unreachable through a `Select` and entirely reachable through a
        // stale deep link or a restored draft written before a topic was
        // renamed. The server answers this one with a 400 and no field.
        problems.push({ field: 'topic', code: 'unknownTopic' });
    }
    /*
     * Optional, and empty is the same as absent: the API stores
     * `subject?.trim() || null`, so a subject of three spaces becomes null
     * there whatever the client believed it sent. Trimmed here so the two
     * agree before the request leaves rather than after.
     */
    const subject = (input.subject ?? '').trim();
    if (subject.length > MAX_SUPPORT_SUBJECT_CHARS) {
        problems.push({ field: 'subject', code: 'tooLong', max: MAX_SUPPORT_SUBJECT_CHARS });
    }
    /*
     * Required, which is stricter than the API — `body` is optional on
     * `OpenThreadDto` because the Message button on a booking row opens a
     * thread without putting words in anybody's mouth. Nobody asks Zuruflex for
     * help without saying what happened, and a support thread that arrives
     * empty is one an agent has to open in order to learn there is nothing
     * in it.
     *
     * Stricter again on whitespace: the server's `MinLength(1)` is satisfied
     * by a single space, which would file a ticket whose first bubble is
     * blank.
     */
    const body = normaliseMessage(input.body ?? '');
    if (body.length === 0) {
        problems.push({ field: 'body', code: 'required' });
    }
    else if (body.length > MAX_MESSAGE_CHARS) {
        problems.push({ field: 'body', code: 'tooLong', max: MAX_MESSAGE_CHARS });
    }
    if (problems.length > 0)
        return { valid: false, problems };
    return {
        valid: true,
        // Narrowed by the checks above; `topic` is a member of THREAD_TOPICS.
        value: { topic: topic, subject: subject || null, body },
    };
}
// ── Whether to ask how it went ───────────────────────────────
/**
 * How long somebody is left alone after being asked once.
 *
 * The rule that decides whether CSAT is a signal or an irritation. A
 * requester who resolves three tickets in a week is one person having one
 * bad week, and asking them three times gets two ignored prompts and one
 * score that is really a verdict on the week — which is the noise that
 * makes the average unreadable.
 */
export const CSAT_ASK_COOLDOWN_DAYS = 7;
/**
 * How stale a resolution may be and still be worth asking about.
 *
 * The prompt lives on a thread, and a thread is reopened by looking at
 * it. Somebody scrolling back to a fortnight-old conversation is not being
 * asked how the help went; they are being asked to remember. A score given
 * then measures their memory, and the desk it grades has moved on.
 */
export const CSAT_ASK_MAX_AGE_DAYS = 14;
const DAY_MS = 86_400_000;
function instant(value) {
    if (value === null || value === undefined)
        return null;
    const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
}
/**
 * Whether to draw the prompt on this thread, for this person, now.
 *
 * The first four conditions are the API's own contract restated where a
 * client can read it: `csatAskedAt` is stamped at `RESOLVED` on a
 * `SUPPORT` thread and only when the operator has the setting on, so a
 * surface that checks the stamp inherits the switch without knowing it
 * exists. A `DIRECT` thread is never eligible — nobody grades a
 * conversation with the person they are hiring a car from — and it is
 * checked here as well as there because a client with a stale thread in
 * its cache is the case both halves exist for.
 *
 * The last two are this file's own, and they are the ones no surface
 * should be trusted to reinvent: asked at most once a week, and never
 * about something that finished a fortnight ago.
 *
 * Both bounds are inclusive of their boundary — exactly seven days since
 * the last ask is a week having passed, exactly fourteen days old is not
 * *more* than a fortnight. A rule written the other way gets a prompt that
 * appears on the eighth day for somebody in one timezone and the ninth for
 * somebody in another, which is a bug nobody ever reproduces.
 */
export function shouldAskCsat(thread, options) {
    if (thread.kind !== 'SUPPORT')
        return false;
    if (thread.status !== 'RESOLVED')
        return false;
    const csat = thread.csat;
    if (csat === null)
        return false;
    const askedAt = instant(csat.askedAt);
    if (askedAt === null)
        return false;
    // Answered is answered, whichever half of the row says so. A score with
    // no `answeredAt` is a row written by an older client; an `answeredAt`
    // with no score is somebody who sent a comment and no stars.
    if (csat.score !== null || instant(csat.answeredAt) !== null)
        return false;
    if (instant(options.dismissedAt) !== null)
        return false;
    const now = options.now.getTime();
    const lastAskedAt = instant(options.lastAskedAt);
    if (lastAskedAt !== null && now - lastAskedAt < CSAT_ASK_COOLDOWN_DAYS * DAY_MS)
        return false;
    // `askedAt` as the fallback clock: the stamp goes on at the moment of
    // resolution, so it dates the same event when `resolvedAt` is missing —
    // which it is on a thread resolved, reopened and resolved again, where
    // the second resolution clears nothing and the first stamp stands.
    const finishedAt = instant(thread.resolvedAt) ?? askedAt;
    if (now - finishedAt > CSAT_ASK_MAX_AGE_DAYS * DAY_MS)
        return false;
    return true;
}
/**
 * Where the amber band starts, as a fraction of the target.
 *
 * The last fifth: on the default hour-to-first-reply that is twelve
 * minutes of warning, which is long enough to pick a thread up and short
 * enough that the band is not simply where everything lives. A fraction
 * rather than a fixed number of minutes, so it scales with an operator who
 * sets a four-hour target and with one who sets fifteen minutes.
 */
export const SLA_DUE_FRACTION = 0.8;
const MINUTE_MS = 60_000;
/**
 * One verdict for the clock that is actually running.
 *
 * Which clock that is follows from the thread rather than from the caller:
 * before a person has replied, the thread is being judged on the first
 * response, and after that on the resolution. Showing both at once is a
 * *display* decision and the inbox row has room for one — this answers the
 * question every surface asks, which is what colour this row is.
 *
 * Both clocks run from `createdAt`, including the second: somebody has
 * been waiting since they wrote, not since we got round to them, and
 * restarting the clock at the first reply would let a desk manufacture a
 * good resolution figure by answering "looking into it" quickly.
 *
 * `paused` covers snoozed, resolved and closed. A snooze is an agreement
 * that there is nothing to do until Thursday, and a resolved thread has
 * stopped; leaving either accumulating minutes gives an inbox a permanent
 * band of red that means nothing happened rather than something is wrong,
 * and a red row that is always red is a row nobody looks at. It covers
 * `DIRECT` too — there is no desk on the other end of a conversation
 * between a renter and an owner, and no target they agreed to.
 *
 * Note that this deliberately reads differently from a *report*: a thread
 * that was resolved late is a breach in the figures and is `paused` here,
 * because this answers "does anybody need to do something about this now".
 */
export function slaState(thread, targets, now = new Date()) {
    if (thread.kind !== 'SUPPORT')
        return 'paused';
    if (thread.status === 'SNOOZED' || thread.status === 'RESOLVED' || thread.status === 'CLOSED') {
        return 'paused';
    }
    const answered = instant(thread.firstResponseAt) !== null;
    const targetMinutes = answered
        ? targets.resolutionTargetHours * 60
        : targets.firstResponseTargetMinutes;
    // No target is not a target of zero. A settings row that failed to load
    // must leave every row unjudged rather than paint the whole inbox red on
    // the strength of a missing number.
    if (!Number.isFinite(targetMinutes) || targetMinutes <= 0)
        return 'paused';
    const startedAt = instant(thread.createdAt);
    if (startedAt === null)
        return 'paused';
    const elapsed = now.getTime() - startedAt;
    const target = targetMinutes * MINUTE_MS;
    // Strictly past, so a thread exactly at its target has not breached yet
    // — the same boundary the desk's own indicator draws, and the side that
    // does not accuse anybody of being late at the instant they are not.
    if (elapsed > target)
        return 'breached';
    if (elapsed >= target * SLA_DUE_FRACTION)
        return 'due';
    return 'ok';
}
